import {
  type KeyboardEvent,
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Macro } from 'src/graphql';
import { VisibleListEntry } from 'src/pages/Conversations/Chat/Macros/macros-modal/types';

interface UseMacroListNavigationInput {
  visibleEntries: VisibleListEntry[];
  onSelectMacro: (macro: Macro) => void | Promise<void>;
  onToggleFolder: (folderId: string) => void;
}

export const useMacroListNavigation = ({
  visibleEntries,
  onSelectMacro,
  onToggleFolder,
}: UseMacroListNavigationInput): {
  highlightedIndex: number;
  listItemRefs: MutableRefObject<(HTMLDivElement | null)[]>;
  handleSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  handleListKeyDown: (event: KeyboardEvent<HTMLUListElement>) => void;
  resetHighlight: () => void;
  resetNavigation: () => void;
} => {
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const resetHighlight = useCallback(() => {
    setHighlightedIndex(0);
  }, []);

  const resetNavigation = useCallback(() => {
    setHighlightedIndex(0);
    listItemRefs.current = [];
  }, []);

  useEffect(() => {
    if (!visibleEntries.length) {
      setHighlightedIndex(0);
      return;
    }

    setHighlightedIndex((current) => Math.min(current, visibleEntries.length - 1));
  }, [visibleEntries.length]);

  useEffect(() => {
    listItemRefs.current = [];
  }, [visibleEntries.length]);

  useEffect(() => {
    const el = listItemRefs.current[highlightedIndex];
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleNavigationKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!visibleEntries.length) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((current) => (current + 1) % visibleEntries.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex(
          (current) => (current - 1 + visibleEntries.length) % visibleEntries.length,
        );
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const entry = visibleEntries[highlightedIndex];
        if (!entry) {
          return;
        }

        if (entry.type === 'macro') {
          void onSelectMacro(entry.macro);
          return;
        }

        if (entry.folderId) {
          onToggleFolder(entry.folderId);
        }
      }
    },
    [highlightedIndex, onSelectMacro, onToggleFolder, visibleEntries],
  );

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      handleNavigationKeyDown(event);
    },
    [handleNavigationKeyDown],
  );

  const handleListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      handleNavigationKeyDown(event);
    },
    [handleNavigationKeyDown],
  );

  return {
    highlightedIndex,
    listItemRefs,
    handleSearchKeyDown,
    handleListKeyDown,
    resetHighlight,
    resetNavigation,
  };
};
